import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import { EMPTY, map, type Observable } from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { type CanvasElementId } from '@blaze-runtime/engine-domain';
import {
  type Connection,
  findFirstNodeInWorkflowOfType,
  isApiCallNode,
  isGetNode,
  type NamespaceId,
  type Workflow,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { RuntimeFacade, type State } from '../../+state';
import {
  createConnection,
  getUiRuntimeElement,
  getWorkflowRuntimeElement,
} from '../../utils/runtime';
import { WorkflowCrudService } from '../workflow-crud.service';

/**
 * Wires an element up to the workflow that feeds it.
 *
 * This is the editor-time half of data binding: choosing a data source for an element creates the
 * workflow that fetches it, and re-choosing one rewrites that workflow's source node in place so
 * that everything the author built on top of it survives.
 */
@Injectable({ providedIn: 'root' })
export class ElementInputWorkflowService {
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly store = inject(Store<State>);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  /** Creates the workflow that feeds an element, and the connection carrying its output there. */
  generateElementInputWorkflow(
    projectId: string,
    elementId: CanvasElementId,
    ns: NamespaceId,
    sourceNode?: WorkflowNode,
    isServer = false,
  ): Observable<{ workflow: Workflow; connection: Connection }> {
    return this.workflowCrudService
      .createWorkflow({
        inputs: {},
        inputSchemas: [],
        isLongRunning: false,
        isServer,
        nodes: sourceNode ? [sourceNode] : [],
        pageId: ns,
        projectId,
        startNodeId: sourceNode?.id ?? null,
      })
      .pipe(
        map((workflow) => ({
          workflow,
          connection: createConnection(
            ns,
            getWorkflowRuntimeElement(workflow.id),
            getUiRuntimeElement(elementId),
            'data-ready',
          ),
        })),
      );
  }

  /**
   * Points an existing workflow at a different data source, and re-runs it.
   *
   * The node to replace is matched by id and type, falling back to whichever node fetches data at
   * all. A source node of the same type keeps its optional configuration, since the author's
   * choices there still apply; a different type replaces it outright.
   *
   * @param resetNodes whether to discard the rest of the workflow rather than keep it around the
   * new source node.
   * @returns nothing when the workflow is not in the state.
   */
  addOrUpdateWorkflowSourceNode(
    workflowId: string,
    sourceNode: WorkflowNode,
    resetNodes = false,
  ): Observable<Workflow> {
    const workflow = this.runtimeFacade.getWorkflowById(workflowId);
    if (!workflow) {
      return EMPTY;
    }

    const updated = produce(workflow, (draft) => {
      if (resetNodes) {
        draft.inputs = {};
        draft.nodes = [];
        draft.startNodeId = null;
      }

      const node =
        draft.nodes.find((n) => n.id === sourceNode.id && n.nodeType === sourceNode.nodeType) ??
        findFirstNodeInWorkflowOfType(draft, isGetNode) ??
        findFirstNodeInWorkflowOfType(draft, isApiCallNode);

      if (node) {
        const nodeIndex = draft.nodes.findIndex((n) => n === node);
        const preserveNode = node.nodeType === sourceNode.nodeType;
        draft.nodes[nodeIndex] = {
          ...(preserveNode ? node : undefined),
          ...sourceNode,
          // The node keeps its place in the workflow, whatever it now fetches.
          id: node.id,
          nextNodeId: node.nextNodeId,
        };
      } else {
        draft.nodes.unshift(sourceNode);
      }

      if (!draft.startNodeId) {
        draft.startNodeId = draft.nodes[0].id;
      }
    });

    return this.workflowCrudService.updateWorkflow({ ...updated, id: workflow.id }).pipe(
      map((savedWorkflow) => {
        this.store.dispatch(runtimeActions.workflowUpdate({ workflow: savedWorkflow }));
        this.store.dispatch(runtimeActions.executeWorkflow({ workflowId: savedWorkflow.id }));
        return savedWorkflow;
      }),
    );
  }
}
